/**
 * BuilderPage
 *
 * Mirrors OperatorPage's two-column layout — narrow collapsible chat panel on
 * the left, dominant canvas on the right — but is hard-wired to the
 * Architect agent and persists its single conversation via user preferences
 * (no per-conversation routing).
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import type { ThreadMessageLike } from '@assistant-ui/react';
import { MessageSquare } from 'lucide-react';
import { cn } from '@/lib/cn';
import {
  ConnectionIndicator,
  CollapsedPanelBar,
  type CollapsedPanel,
} from '../../components/ui';
import OperatorChatPanel from '../operator/OperatorChatPanel';
import OperatorLoadingSkeleton from '../operator/OperatorLoadingSkeleton';
import { UIEventProvider, useUIEvent } from '../../contexts/UIEventContext';
import { ThoughtsProvider } from '../../contexts/ThoughtsContext';
import { CanvasProvider } from '../../contexts/CanvasContext';
import { CanvasPanel } from '../../components/canvas/CanvasPanel';
import { CanvasUrlSync } from '../../components/canvas/CanvasUrlSync';
import { CanvasHotkeys } from '../../components/canvas/CanvasHotkeys';
import { useAgentRuntime } from '../../hooks/useAgentRuntime';
import { useAgentRunEvents } from '../../hooks/useAgentRunEvents';
import { useResumeInFlightRun } from '../../hooks/useResumeInFlightRun';
import { useDispatchAction } from '../../hooks/useDispatchAction';
import {
  useConversationRestore,
  type CanvasHint,
} from '../../hooks/useConversationRestore';
import { useConversationNamedEvent } from '../../hooks/useConversationNamedEvent';
import { useMeAgent as useAgent } from '../../hooks/useAgents';
import { useMeConversations } from '../../hooks/useConversations';
import { useMeSpaces } from '../../hooks/useMeSpaces';
import { useToolNameMap, ToolNameProvider } from '../../hooks/useTools';
import { usePlatformEvents } from '../../contexts/PlatformEventContext';
import { useAuth } from '../../store/auth-context';
import { resolveDefaultSpaceId } from '../../lib/default-space';
import type { Conversation } from '../../types';

const CHAT_COLLAPSED_KEY = 'builder-chat-collapsed';
const ARCHITECT_AGENT_ID = import.meta.env.VITE_ARCHITECT_AGENT_ID as string;

interface BuilderPageProps {
  visible?: boolean;
}

export default function BuilderPage({ visible = true }: BuilderPageProps) {
  const { user, updatePreferences } = useAuth();
  const [conversationKey, setConversationKey] = useState(0);
  // Track the active conversation locally so the resolved spaceId survives a
  // token refresh that wipes `user.preferences` (the /auth/refresh endpoint
  // doesn't echo back preferences).
  const [activeConversationId, setActiveConversationId] = useState<string | undefined>(
    user?.preferences?.builderConversationId ?? undefined,
  );

  useConversationNamedEvent();

  const { data: conversationsData } = useMeConversations();
  const { data: spacesData } = useMeSpaces();

  // Prefer the conversation's own spaceId (anchored to the conversation itself,
  // unaffected by auth state changes). Fall back to the user's personal/default
  // space when there's no active conversation yet — e.g. first Builder visit.
  const resolvedSpaceId = useMemo(() => {
    const fromConversation = activeConversationId
      ? (conversationsData?.items.find(
          (c) => c.conversationId === activeConversationId,
        ) as (Conversation & { spaceId?: string }) | undefined)?.spaceId
      : undefined;
    return (
      fromConversation ??
      resolveDefaultSpaceId({
        spaces: spacesData?.items ?? [],
        lastSpaceId: user?.preferences?.lastSpaceId,
        defaultSpaceId: user?.defaultSpaceId,
      })
    );
  }, [
    activeConversationId,
    conversationsData,
    spacesData,
    user?.preferences?.lastSpaceId,
    user?.defaultSpaceId,
  ]);

  const handleNewConversation = useCallback(async () => {
    try {
      await updatePreferences({ builderConversationId: null });
    } catch {
      // Best-effort — still reset the conversation even if the preference clear fails
    }
    setActiveConversationId(undefined);
    setConversationKey((k) => k + 1);
  }, [updatePreferences]);

  const handleConversationChange = useCallback(
    (conversationId: string) => {
      setActiveConversationId(conversationId);
      updatePreferences({ builderConversationId: conversationId });
    },
    [updatePreferences],
  );

  return (
    <UIEventProvider spaceId={resolvedSpaceId}>
      <ThoughtsProvider>
        <BuilderConversationLoader
          key={conversationKey}
          visible={visible}
          initialConversationId={user?.preferences?.builderConversationId ?? undefined}
          spaceId={resolvedSpaceId}
          onNewConversation={handleNewConversation}
          onConversationChange={handleConversationChange}
        />
      </ThoughtsProvider>
    </UIEventProvider>
  );
}

interface BuilderLoaderProps {
  visible: boolean;
  initialConversationId?: string;
  spaceId?: string;
  onNewConversation: () => void;
  onConversationChange: (conversationId: string) => void;
}

function BuilderConversationLoader({
  visible,
  initialConversationId,
  spaceId,
  onNewConversation,
  onConversationChange,
}: BuilderLoaderProps) {
  const { dispatchUIEvent } = useUIEvent();
  const { user } = useAuth();
  const {
    messages: initialMessages,
    canvasHints,
    defaultCanvas,
    loading,
    inFlightRun,
  } = useConversationRestore({
    conversationId: initialConversationId,
    spaceId,
    dispatchUIEvent,
    canvasEventTypes: ['show_entity'],
  });

  const ready = !loading;
  const [skeletonMounted, setSkeletonMounted] = useState(true);
  const [skeletonVisible, setSkeletonVisible] = useState(true);

  // Mount the thread one frame AFTER `visible` flips true. Mounting the
  // assistant-ui runtime + Lexical composer synchronously inside the router's
  // navigation commit crashes @assistant-ui/tap under StrictMode ("Version is
  // less than committed version" from ComposerPrimitive.TriggerPopover) and
  // unmounts the whole app. Deferring to a rAF gives the mount its own clean
  // commit. Direct URL loads never hit this (the thread mounts via the async
  // ready flip), but SPA navigation to /builder did.
  const [threadMounted, setThreadMounted] = useState(visible);
  useEffect(() => {
    if (!visible) {
      setThreadMounted(false);
      return;
    }
    const r = requestAnimationFrame(() => setThreadMounted(true));
    return () => cancelAnimationFrame(r);
  }, [visible]);

  useEffect(() => {
    if (!ready) return;
    const r = requestAnimationFrame(() => setSkeletonVisible(false));
    const t = setTimeout(() => setSkeletonMounted(false), 600);
    return () => {
      cancelAnimationFrame(r);
      clearTimeout(t);
    };
  }, [ready]);

  if (!visible) return null;

  return (
    <div className="relative h-screen">
      {ready && threadMounted && (
        <div
          className={cn(
            'absolute inset-0',
            // Skip the entry fade in hidden tabs — Safari freezes keyframe
            // animations started while backgrounded at opacity 0 (see the
            // matching gate in OperatorPage).
            (typeof document === 'undefined' || document.visibilityState === 'visible') &&
              'animate-in fade-in duration-500',
          )}
        >
          <BuilderThread
            visible={visible}
            initialConversationId={initialConversationId}
            spaceId={spaceId}
            onNewConversation={onNewConversation}
            onConversationChange={onConversationChange}
            initialMessages={initialMessages}
            canvasHints={canvasHints}
            defaultCanvas={defaultCanvas}
            inFlightRun={inFlightRun}
          />
        </div>
      )}
      {skeletonMounted && (
        <div
          className={cn(
            'absolute inset-0 transition-opacity duration-500 ease-in-out',
            skeletonVisible ? 'opacity-100' : 'pointer-events-none opacity-0',
          )}
        >
          <OperatorLoadingSkeleton
            chatWidth={user?.preferences?.builderChatWidth}
            chatTitle="Builder"
          />
        </div>
      )}
    </div>
  );
}

interface BuilderThreadProps extends BuilderLoaderProps {
  initialMessages?: readonly ThreadMessageLike[];
  canvasHints?: CanvasHint[];
  defaultCanvas?: import('../../hooks/useConversationRestore').ConversationRestoreResult['defaultCanvas'];
  inFlightRun?: import('../../hooks/useConversationRestore').ConversationRestoreResult['inFlightRun'];
}

function BuilderThread({
  visible,
  initialConversationId,
  spaceId,
  onNewConversation,
  onConversationChange,
  initialMessages,
  canvasHints,
  defaultCanvas,
  inFlightRun,
}: BuilderThreadProps) {
  const { dispatchUIEvent } = useUIEvent();
  const { user, updatePreferences } = useAuth();
  // Track the conversation id reactively so the background-run subscription is
  // scoped to THIS conversation server-side (it starts undefined for a new
  // chat, then updates when the first message creates the conversation).
  const [liveConversationId, setLiveConversationId] = useState<string | undefined>(
    initialConversationId,
  );
  const handleConversationChange = useCallback(
    (id: string) => {
      setLiveConversationId(id);
      onConversationChange(id);
    },
    [onConversationChange],
  );
  const { runtime, conversationIdRef, activeRunIdStore, getToolName } = useAgentRuntime({
    agentId: ARCHITECT_AGENT_ID,
    spaceId,
    initialConversationId,
    onConversationChange: handleConversationChange,
    initialMessages,
    dispatchUIEvent,
  });
  useAgentRunEvents(
    runtime,
    activeRunIdStore,
    conversationIdRef,
    dispatchUIEvent,
    getToolName,
    visible,
    liveConversationId,
  );
  // Reloaded mid-run: re-attach the still-executing run so its live trace
  // resumes instead of looking lost.
  useResumeInFlightRun({ runtime, inFlightRun, enabled: visible });

  const { data: agent } = useAgent(ARCHITECT_AGENT_ID);
  const toolNameMap = useToolNameMap(agent?.tools);

  const handleAction = useDispatchAction(
    ARCHITECT_AGENT_ID,
    spaceId,
    conversationIdRef,
  );
  const { isConnected, diagnostics } = usePlatformEvents();

  const [chatCollapsed, setChatCollapsed] = useState(
    () => localStorage.getItem(CHAT_COLLAPSED_KEY) === 'true',
  );
  const toggleChat = useCallback(() => {
    setChatCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(CHAT_COLLAPSED_KEY, String(next));
      return next;
    });
  }, []);

  const handleChatWidthCommit = useCallback(
    (width: number) => {
      updatePreferences({ builderChatWidth: width }).catch(() => {
        // Best-effort — width persistence isn't critical
      });
    },
    [updatePreferences],
  );

  const collapsedPanels = useMemo<CollapsedPanel[]>(() => {
    const panels: CollapsedPanel[] = [];
    if (chatCollapsed) {
      panels.push({
        id: 'chat',
        label: 'Builder',
        icon: MessageSquare,
        onExpand: toggleChat,
      });
    }
    return panels;
  }, [chatCollapsed, toggleChat]);

  if (!visible) return null;

  return (
    <CanvasProvider
      conversationId={conversationIdRef.current ?? initialConversationId}
      spaceId={spaceId}
      canvasHints={canvasHints}
      initialCanvas={defaultCanvas}
      autoScrollOnUpdate={false}
    >
      <CanvasUrlSync />
      <CanvasHotkeys />
      <ToolNameProvider value={toolNameMap}>
        <div className="flex h-screen flex-col">
          <CollapsedPanelBar panels={collapsedPanels} />

          <div className="flex min-h-0 flex-1">
            <OperatorChatPanel
              runtime={runtime}
              agentId={ARCHITECT_AGENT_ID}
              collapsed={chatCollapsed}
              onToggle={toggleChat}
              onNewChat={onNewConversation}
              initialWidth={user?.preferences?.builderChatWidth}
              onWidthCommit={handleChatWidthCommit}
              header={
                <h2 className="text-sm font-semibold text-foreground">Builder</h2>
              }
            />

            <div className="flex min-w-0 flex-1 flex-col">
              <CanvasPanel
                onAction={handleAction}
                headerActions={
                  <ConnectionIndicator
                    isConnected={isConnected}
                    diagnostics={diagnostics}
                  />
                }
              />
            </div>
          </div>
        </div>
      </ToolNameProvider>
    </CanvasProvider>
  );
}
